/**
 * Chapter 1 — application tour: step definitions + pure phase reducer.
 *
 * Every step targets a REAL UI element registered with
 * data-tutorial-target (see spotlight/domTargets.ts): the desktop Sidebar
 * and the DS2 mobile Drawer render the SAME nav set, so nav steps share one
 * target id and measurement picks whichever instance is visible. Steps are
 * informational — the spotlight blocks activation and Continue advances —
 * so the tour can never navigate, start a game, enter matchmaking, message
 * anyone or modify data.
 *
 * Copy is verified against current behavior:
 *  - Play vs AI creates a bot game immediately on entry (so the tour only
 *    DESCRIBES it) and offers no difficulty selector today — the copy makes
 *    no difficulty claims.
 *  - Leaderboard ranks competitive online play only; bot games are excluded.
 *  - Profile lives inside Settings (the sidebar chip links there); mobile
 *    has no profile chip, so the profile step is desktop-only and the
 *    Settings copy carries the profile mention everywhere.
 */
import { t } from '../i18n';

export interface AppTourStep {
  id: string;
  /** data-tutorial-target id of the real element. */
  target: string;
  /** Mobile: does this target live inside the navigation drawer? */
  inDrawer: boolean;
  /** Step exists only on the desktop shell (e.g. the sidebar profile chip). */
  desktopOnly?: boolean;
  /**
   * LOCALIZATION (Phase 8): namespaced catalog key for the step title. Nav
   * steps reuse the already-localized `navigation:items.*` labels so the
   * coach-card title always matches the spotlighted real control's label;
   * only non-nav steps carry their own `tutorial:tour.*` title.
   */
  titleKey: string;
  /** Namespaced catalog key for the step body (`tutorial:tour.<id>.body`). */
  bodyKey: string;
}

/** Localized title/body for a tour step, resolved in the active language at
 *  call time (renderers re-invoke per render → live relabel on switch). */
export function tourStepTitle(step: AppTourStep): string {
  return String(t(step.titleKey as never));
}
export function tourStepBody(step: AppTourStep): string {
  return String(t(step.bodyKey as never));
}

export const APP_TOUR_STEPS: AppTourStep[] = [
  {
    id: 'home', target: 'home-dashboard', inDrawer: false,
    titleKey: 'navigation:items.home', bodyKey: 'tutorial:tour.home.body',
  },
  {
    id: 'stats', target: 'home-stats', inDrawer: false,
    titleKey: 'navigation:items.stats', bodyKey: 'tutorial:tour.stats.body',
  },
  {
    id: 'play-online', target: 'nav:/play/online', inDrawer: true,
    titleKey: 'navigation:items.playOnline', bodyKey: 'tutorial:tour.play-online.body',
  },
  {
    id: 'play-ai', target: 'nav:/play/ai', inDrawer: true,
    titleKey: 'navigation:items.playBots', bodyKey: 'tutorial:tour.play-ai.body',
  },
  {
    id: 'friends', target: 'nav:/friends', inDrawer: true,
    titleKey: 'navigation:items.friends', bodyKey: 'tutorial:tour.friends.body',
  },
  {
    id: 'messages', target: 'nav:/messages', inDrawer: true,
    titleKey: 'navigation:items.messages', bodyKey: 'tutorial:tour.messages.body',
  },
  {
    id: 'leaderboard', target: 'nav:/leaderboard', inDrawer: true,
    titleKey: 'navigation:items.leaderboard', bodyKey: 'tutorial:tour.leaderboard.body',
  },
  {
    id: 'rules', target: 'nav:/rules', inDrawer: true,
    titleKey: 'navigation:items.rules', bodyKey: 'tutorial:tour.rules.body',
  },
  {
    id: 'profile', target: 'profile-chip', inDrawer: false, desktopOnly: true,
    titleKey: 'tutorial:tour.profile.title', bodyKey: 'tutorial:tour.profile.body',
  },
  {
    id: 'settings', target: 'nav:/settings', inDrawer: true,
    titleKey: 'navigation:items.settings', bodyKey: 'tutorial:tour.settings.body',
  },
];

/** The steps that actually run on the current shell layout. */
export function tourStepsFor(isMobileShell: boolean): AppTourStep[] {
  return APP_TOUR_STEPS.filter((s) => !(isMobileShell && s.desktopOnly));
}

// ── Phase machine ────────────────────────────────────────────────────────

export type AppTourPhase =
  | { kind: 'welcome' }
  | { kind: 'step'; index: number }
  | { kind: 'transition' }   // "Ready to learn the table?" modal
  | { kind: 'done'; next: 'gameplay' | 'exit' };

export type AppTourEvent =
  | { type: 'start' }            // welcome → first step
  | { type: 'continue' }         // step → next step / transition
  | { type: 'back' }             // step → previous step (safe: informational)
  | { type: 'skip-chapter' }     // anywhere → transition
  | { type: 'start-gameplay' }   // transition → done(gameplay)
  | { type: 'exit' };            // anywhere → done(exit)

export const INITIAL_TOUR_PHASE: AppTourPhase = { kind: 'welcome' };

export function reduceAppTour(
  phase: AppTourPhase,
  evt: AppTourEvent,
  stepCount: number,
): AppTourPhase {
  switch (evt.type) {
    case 'start':
      return phase.kind === 'welcome' ? { kind: 'step', index: 0 } : phase;
    case 'continue':
      if (phase.kind !== 'step') return phase;
      return phase.index + 1 >= stepCount
        ? { kind: 'transition' }
        : { kind: 'step', index: phase.index + 1 };
    case 'back':
      if (phase.kind !== 'step') return phase;
      return phase.index === 0 ? { kind: 'welcome' } : { kind: 'step', index: phase.index - 1 };
    case 'skip-chapter':
      return phase.kind === 'done' ? phase : { kind: 'transition' };
    case 'start-gameplay':
      return phase.kind === 'transition' ? { kind: 'done', next: 'gameplay' } : phase;
    case 'exit':
      return { kind: 'done', next: 'exit' };
    default:
      return phase;
  }
}
